import { Extension } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { push as pushToast } from "@lib/toast/toastStore";

// Matches the same {{contact.x}} tokens the backend substitutes per
// recipient at send time (see ContactRepository::replaceContactPlaceholders
// on the API, mirrored client-side in helpers/placeholderValidation.js).
// Job ad emails use {{candidate.x}} tokens instead (the `scope` option).
const placeholderRegex = (scope = "contact") => new RegExp(`\\{\\{${scope}\\.[a-zA-Z_]+\\}\\}`, "g");

const PLACEHOLDER_REMOVED_TITLE = "Placeholder removed";
const PLACEHOLDER_REMOVED_WARNING =
  "Without the placeholder, the recipients' info won't be filled in automatically. Add it back if you'd like to keep the personalisation.";

// duration: 0 — this stays until the user closes it themselves rather than
// auto-dismissing, since silently losing personalization is worth making
// sure they actually saw.
const warnPlaceholderRemoved = () =>
  pushToast({
    title: PLACEHOLDER_REMOVED_TITLE,
    description: PLACEHOLDER_REMOVED_WARNING,
    variant: "warning",
    duration: 0,
  });

const findPlaceholderRanges = (doc, scope = "contact") => {
  const ranges = [];
  const regex = placeholderRegex(scope);
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    regex.lastIndex = 0;
    let match;
    while ((match = regex.exec(node.text))) {
      const from = pos + match.index;
      const to = from + match[0].length;
      ranges.push({ from, to });
    }
  });
  return ranges;
};

const buildDecorations = (doc, className, scope) => {
  const decorations = findPlaceholderRanges(doc, scope).map(({ from, to }) =>
    Decoration.inline(from, to, { class: className })
  );
  return DecorationSet.create(doc, decorations);
};

// Purely a view-layer highlight (ProseMirror decorations) — doesn't touch
// the stored HTML/document, so it can't interfere with typing, AI rewrites,
// or the plain-text placeholder substitution logic elsewhere. Only applies
// a CSS class (no inline color/background) — RichAIField.module.css scopes
// the actual blue styling to `.container.focused`, so the highlight only
// shows while the field is in "edit mode". A collapsed/blurred field is
// technically still editable, but showing the highlight there reads as
// "this is how the sent email will look", which it isn't.
export const PlaceholderHighlight = Extension.create({
  name: "placeholderHighlight",

  addOptions() {
    return { className: "", scope: "contact" };
  },

  addProseMirrorPlugins() {
    const key = new PluginKey("placeholderHighlight");
    const { className, scope } = this.options;

    return [
      new Plugin({
        key,
        state: {
          init(_, { doc }) {
            return buildDecorations(doc, className, scope);
          },
          apply(tr, oldState) {
            // Rebuild on selection changes too. This clears decoration
            // attributes left behind by an older editor instance as soon as
            // a user clicks a token, without requiring them to edit the text.
            return tr.docChanged || tr.selectionSet
              ? buildDecorations(tr.doc, className, scope)
              : oldState;
          },
        },
        props: {
          decorations(state) {
            return key.getState(state);
          },
        },
      }),
    ];
  },

  // Placeholders behave like atomic labels rather than plain text: a single
  // Backspace/Delete removes the WHOLE token in one step (never eats it one
  // character at a time), and doing so warns the user since it silently
  // turns off personalization for that spot.
  addKeyboardShortcuts() {
    const removeWholeToken = (direction) => () => {
      const { state, view } = this.editor;
      const { selection } = state;
      const ranges = findPlaceholderRanges(state.doc, this.options.scope);

      if (selection.empty) {
        const pos = selection.from;
        const probeFrom = direction === "backward" ? pos - 1 : pos;
        const probeTo = direction === "backward" ? pos : pos + 1;
        if (probeFrom < 0) return false;

        const range = ranges.find(
          (r) => probeFrom < r.to && probeTo > r.from
        );
        if (!range) return false;

        view.dispatch(state.tr.delete(range.from, range.to));
        warnPlaceholderRemoved();
        return true;
      }

      // A dragged selection may only partially overlap one or more
      // tokens — widen the deletion to cover every token it touches, so a
      // token is never left half-erased.
      const overlapping = ranges.filter(
        (r) => selection.from < r.to && selection.to > r.from
      );
      if (overlapping.length === 0) return false;

      const from = Math.min(selection.from, ...overlapping.map((r) => r.from));
      const to = Math.max(selection.to, ...overlapping.map((r) => r.to));
      view.dispatch(state.tr.delete(from, to));
      warnPlaceholderRemoved();
      return true;
    };

    return {
      Backspace: removeWholeToken("backward"),
      Delete: removeWholeToken("forward"),
    };
  },
});

export default PlaceholderHighlight;
